/**
 * Typed calls for the analytics reads (docs/analytics.md §5, the backend's
 * src/analytics/http/admin/analytics.ts; staff, role `read`). Every read but realtime takes an
 * inclusive UTC range of at most 90 days; every response says whether any sample data is in it.
 * The backend returns sums and counts; rates, averages and CSV are the console's (format.ts, csv.ts).
 */

import type { AdminClient } from "@/api/client";
import type { DateRange } from "./range";

export interface Totals {
  plays: number;
  engagedPlays: number;
  watchSeconds: number;
  viewers: number;
  completed: number;
}

export interface DayPoint {
  day: string;
  plays: number;
  watchSeconds: number;
  viewers: number;
}

interface Sampled {
  /** Some of it is generated sample data (`npm run stats -- seed`): screens say so. */
  sample: boolean;
}

export interface Summary extends Sampled {
  range: DateRange;
  previous: DateRange;
  totals: Totals;
  previousTotals: Totals;
  series: DayPoint[];
}

export interface ContentRow extends Totals {
  itemId: string;
}

export interface ContentReport extends Sampled {
  /** Every item with plays in the range, most played first. */
  items: ContentRow[];
}

export type PlayMode = "vod" | "live" | "replay" | "offline";

export interface ItemReport extends Sampled {
  totals: Totals;
  previousTotals: Totals;
  series: DayPoint[];
  /** Twenty shares (0–1): the plays that watched each twentieth of the item. */
  retention: number[];
  modes: Array<{ mode: PlayMode; plays: number; watchSeconds: number }>;
}

/** A platform or a country; keys with under 5 viewers come merged as "other". */
export interface DimensionRow {
  key: string;
  plays: number;
  watchSeconds: number;
  viewers: number;
}

export interface AudienceReport extends Sampled {
  newViewers: number;
  returningViewers: number;
  platforms: DimensionRow[];
  countries: DimensionRow[];
}

export interface LiveEventRow {
  itemId: string;
  peak: number;
  /** The ISO UTC minute of the peak, or null when it was only watched as a replay in the range. */
  peakAt: string | null;
  liveViewers: number;
  replayViewers: number;
  liveWatchSeconds: number;
  replayWatchSeconds: number;
}

export interface LiveReport extends Sampled {
  events: LiveEventRow[];
}

export interface Realtime extends Sampled {
  /** Sessions heard from in the last 150 s. */
  watchingNow: number;
  live: Array<{ itemId: string; watchingNow: number }>;
  /** Sixty entries, oldest first; `minute` is the ISO UTC start of the minute. */
  lastHour: Array<{ minute: string; plays: number }>;
}

export interface SaleRow {
  productId: string;
  /** Null for a product the deployment no longer lists. */
  kind: "subscription" | "purchase" | "rental" | null;
  count: number;
  /** The display price from the deployment ("£4.99"), or null. */
  price: string | null;
}

export interface MoneyReport extends Sampled {
  subscribers: { active: number; started: number; ended: number };
  sales: SaleRow[];
  comps: { granted: number; codesRedeemed: number };
  series: Array<{ day: string; started: number; ended: number; sales: number }>;
}

const BASE = "/admin/v1/analytics";
const query = (r: DateRange) => `?from=${r.from}&to=${r.to}`;

export const analytics = {
  summary: (c: AdminClient, r: DateRange) => c.call<Summary>("GET", `${BASE}/summary${query(r)}`),
  content: (c: AdminClient, r: DateRange) => c.call<ContentReport>("GET", `${BASE}/content${query(r)}`),
  item: (c: AdminClient, itemId: string, r: DateRange) => c.call<ItemReport>("GET", `${BASE}/items/${encodeURIComponent(itemId)}${query(r)}`),
  audience: (c: AdminClient, r: DateRange) => c.call<AudienceReport>("GET", `${BASE}/audience${query(r)}`),
  live: (c: AdminClient, r: DateRange) => c.call<LiveReport>("GET", `${BASE}/live${query(r)}`),
  realtime: (c: AdminClient) => c.call<Realtime>("GET", `${BASE}/realtime`),
  money: (c: AdminClient, r: DateRange) => c.call<MoneyReport>("GET", `${BASE}/money${query(r)}`),
};
